/**
 * Width tiers for the header, measured on the header ELEMENT rather than the
 * viewport. The header lives inside a sidebar + a fixed icon tray, so viewport
 * width says almost nothing about the space this row actually has; a media
 * query would collapse labels on a wide screen with the sidebar open and leave
 * them squeezed on a narrow one without it.
 *
 * Order of sacrifice as space runs out: decorative context first (the type
 * chip's year suffix, dividers), then labels that repeat information already
 * on screen (the month name inside "Lock Aug", which is also the month
 * selector), then the labels themselves — never the control.
 */
export type HeaderTier = 'xl' | 'lg' | 'md' | 'sm';

export interface HeaderLayout {
  tier: HeaderTier;
  showYearArrows: boolean;
  showLockLabel: boolean;
  showLockMonthName: boolean;
  showTypeChip: boolean;
  showStatusPill: boolean;
  showDividers: boolean;
  propertyMaxWidth: number;
  versionMaxWidth: number;
}

export function tierFor(width: number): HeaderTier {
  if (width >= 1180) return 'xl';
  if (width >= 980) return 'lg';
  if (width >= 800) return 'md';
  return 'sm';
}

/** Pure so the collapse order is a testable contract, not a pile of inline ternaries. */
export function headerLayoutFor(width: number): HeaderLayout {
  const tier = tierFor(width);
  return {
    tier,
    showYearArrows: tier === 'xl' || tier === 'lg',
    showLockLabel: tier !== 'sm',
    showLockMonthName: tier === 'xl',
    showTypeChip: tier !== 'sm',
    showStatusPill: tier !== 'sm',
    showDividers: tier !== 'sm',
    // A generous CAP, not a budget: the left group is the flexible one, so the
    // property name truncates when the row genuinely runs out of space. A tight
    // per-tier cap clipped "Oshkosh Marriott Waterfront" to "Oshkos…" while
    // there was still visible room to its right.
    propertyMaxWidth: { xl: 280, lg: 260, md: 240, sm: 180 }[tier],
    versionMaxWidth: { xl: 140, lg: 120, md: 96, sm: 72 }[tier],
  };
}
